import { useState, useEffect } from 'react';

const GECKOTERMINAL_API = 'https://api.geckoterminal.com/api/v2';
const INK_NETWORK = 'ink';

export interface CandleData {
  time: number; // Unix timestamp in seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export type ChartTimeframe = '5M' | '15M' | '1H' | '4H' | '1D';

interface TimeframeConfig {
  timeframe: 'minute' | 'hour' | 'day';
  aggregate: number;
  limit: number;
}

interface GeckoTerminalOHLCV {
  data: {
    attributes: {
      ohlcv_list: [number, number, number, number, number, number][]; // [time, open, high, low, close, volume]
    };
  };
  meta: {
    base: {
      address: string;
      name: string;
      symbol: string;
    };
    quote: {
      address: string;
      name: string;
      symbol: string;
    };
  };
}

/**
 * Map UI timeframe selector to GeckoTerminal API parameters
 */
const getTimeframeConfig = (timeframe: ChartTimeframe): TimeframeConfig => {
  switch (timeframe) {
    case '5M':
      return { timeframe: 'minute', aggregate: 5, limit: 288 }; // 24 hours
    case '15M':
      return { timeframe: 'minute', aggregate: 15, limit: 672 }; // 7 days
    case '1H':
      return { timeframe: 'hour', aggregate: 1, limit: 168 }; // 7 days
    case '4H':
      return { timeframe: 'hour', aggregate: 4, limit: 180 }; // 30 days
    case '1D':
      return { timeframe: 'day', aggregate: 1, limit: 90 }; // 90 days
    default:
      return { timeframe: 'minute', aggregate: 15, limit: 672 }; // Default to 15M
  }
};

/**
 * Get pool address for a token on specified network
 * First checks GeckoTerminal for pools containing this token
 */
const getPoolAddress = async (tokenAddress: string, network: string = INK_NETWORK): Promise<string | null> => {
  try {
    // Search for pools on network containing this token
    const url = `${GECKOTERMINAL_API}/networks/${network}/tokens/${tokenAddress}/pools?page=1`;
    const response = await fetch(url);

    if (!response.ok) {
      console.error('[GeckoTerminal] Failed to fetch pools:', response.status);
      return null;
    }

    const data = await response.json();

    // Get the first (most liquid) pool
    if (data.data && data.data.length > 0) {
      const poolAddress = data.data[0].id.split('_')[1]; // Format: "network_0xPOOLADDRESS"
      console.log('[GeckoTerminal] Found pool:', poolAddress);
      return poolAddress;
    }

    console.warn('[GeckoTerminal] No pools found for token:', tokenAddress);
    return null;
  } catch (error) {
    console.error('[GeckoTerminal] Error fetching pool address:', error);
    return null;
  }
};

/**
 * Fetch historical OHLCV data from GeckoTerminal
 * @param poolAddress - The pool address on network
 * @param timeframe - 'minute', 'hour', or 'day'
 * @param aggregate - Aggregation period (e.g., 15 for 15-minute candles)
 * @param limit - Number of candles to fetch (max 1000)
 * @param network - Network identifier (default: ink)
 */
const fetchOHLCVData = async (
  poolAddress: string,
  timeframe: 'minute' | 'hour' | 'day' = 'minute',
  aggregate: number = 15,
  limit: number = 168,
  network: string = INK_NETWORK
): Promise<CandleData[]> => {
  try {
    const url = `${GECKOTERMINAL_API}/networks/${network}/pools/${poolAddress}/ohlcv/${timeframe}?aggregate=${aggregate}&limit=${limit}`;
    console.log('[GeckoTerminal] Fetching data from:', url);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch OHLCV data: ${response.status}`);
    }

    const data: GeckoTerminalOHLCV = await response.json();

    // Transform GeckoTerminal format to our CandleData format
    const candles: CandleData[] = data.data.attributes.ohlcv_list.map(candle => ({
      time: candle[0], // Unix timestamp (already in seconds)
      open: candle[1],
      high: candle[2],
      low: candle[3],
      close: candle[4],
      volume: candle[5]
    }));

    console.log('[GeckoTerminal] Fetched', candles.length, 'candles');
    return candles;
  } catch (error) {
    console.error('[GeckoTerminal] Error fetching OHLCV data:', error);
    throw error;
  }
};

/**
 * Hook to fetch real historical chart data from GeckoTerminal
 */
export const useTokenChartData = (tokenAddress: string | null, timeframe: ChartTimeframe = '15M', network: string = 'ink') => {
  const [data, setData] = useState<CandleData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenAddress) {
      setLoading(false);
      return;
    }

    const fetchChartData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Step 1: Get pool address for this token
        const poolAddress = await getPoolAddress(tokenAddress);

        if (!poolAddress) {
          throw new Error('No trading pool found for this token');
        }

        // Step 2: Get timeframe configuration
        const config = getTimeframeConfig(timeframe);
        console.log('[useTokenChartData] Fetching', timeframe, 'data with config:', config);

        // Step 3: Fetch OHLCV data from GeckoTerminal with selected timeframe
        let candles = await fetchOHLCVData(poolAddress, config.timeframe, config.aggregate, config.limit);

        // If we got very few candles, try with fallback
        // Only fall back if we have fewer than 2 valid candles
        if (candles.length < 2) {
          console.log('[useTokenChartData] Not enough data at requested timeframe, trying fallback...');
          candles = await fetchOHLCVData(poolAddress, 'hour', 1, 168);
        }

        if (candles.length < 2) {
          candles = await fetchOHLCVData(poolAddress, 'day', 1, 30);
        }

        // Validate minimum data requirement
        if (candles.length < 2) {
          throw new Error('Insufficient trading history for this token. Chart requires at least 2 data points.');
        }

        console.log('[useTokenChartData] Successfully fetched', candles.length, 'candles');
        setData(candles);
        setLoading(false);
      } catch (err) {
        console.error('[useTokenChartData] Error fetching chart data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load chart data');
        setLoading(false);
      }
    };

    fetchChartData();
  }, [tokenAddress, timeframe]);

  return { data, loading, error };
};
